import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  SavedDataMultipoint,
  SaveDraftMultiPoint,
  SocialReasonMultiPoint,
  CheckCups, CheckInstallations,
  CreateContractMultipoint,
  BusinessMultiPoint,
  PreviousInstallation,
  InstallationMultiPoint,
  SignerMultiPoint,
  MultipointGetCampaignRequest
} from './new-contract-pymes-multipoint.model';
import merge from 'ramda/src/merge';
import { CheckIbanSRRequest, CheckIbanSRResponse, GenericResponse } from '@interfaces';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '@commons';
import { UtilService } from '@services/util.service';

@Injectable({
  providedIn: 'root'
})
export class NewContractPymesMultipointService {
  private savedDataMultipoint = new BehaviorSubject<SavedDataMultipoint>(null);
  private loaderInstallationMultipoint = new BehaviorSubject<number>(0);

  constructor(private http: HttpClient, private config: ConfigService, private utilService: UtilService) { }

  public getSavedDataMultipoint(): BehaviorSubject<SavedDataMultipoint> {
    return this.savedDataMultipoint;
  }

  public setSavedDataMultipoint(data: any): void {
    const dataToSave = merge(this.getSavedDataMultipoint().value || {}, data);
    this.savedDataMultipoint.next(dataToSave);
  }

  public resetSavedDataMultipoint(): void {
    this.savedDataMultipoint.next(null);
  }

  public queryCifMultiPoint(cif: string, codCrmUser: string): Observable<GenericResponse> {
    return this.http.put<GenericResponse>(this.config.getUrl('contrato.queryCifMultiPoint', { cif, codCrmUser }), {});
  }

  public saveDraftMultiPoint(idBorrador: number, params: SaveDraftMultiPoint): Observable<GenericResponse> {
    return this.http.put<GenericResponse>(this.config.getUrl('contrato.saveDraftMultiPoint', { idBorrador }), params);
  }

  public getDraftMultiPoint(idBorrador: string): Observable<GenericResponse> {
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getDraftMultiPoint', { idBorrador }));
  }

  public socialReasonMultiPoint(params: SocialReasonMultiPoint): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('contrato.socialReasonMultipoint'), params);
  }

  public validateCups(cups: string, idBorrador: number): Observable<GenericResponse<CheckCups>> {
    return this.http.put<GenericResponse>(this.config.getUrl('contrato.validateCups', { cups, idBorrador }), {});
  }

  public postInstallation(params: CheckInstallations): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('contrato.postInstallation'), params);
  }

  public getLoaderInstallations(): BehaviorSubject<number> {
    return this.loaderInstallationMultipoint;
  }

  public setLoaderInstallations(plus: boolean): void {
    let activesCalls = this.loaderInstallationMultipoint.value;
    plus ? activesCalls++ : activesCalls--;
    this.loaderInstallationMultipoint.next(activesCalls);
  }

  public resetLoaderInstallations(): void {
    this.loaderInstallationMultipoint.next(0);
  }

  public getCampaignsMultipoint(url: string, params: MultipointGetCampaignRequest): Observable<GenericResponse> {
    return this.http.get<GenericResponse>(this.config.getUrl(url, { idBorrador: params.idBorrador }), { params: params as any });
  }

  public createContractMultipoint(params: CreateContractMultipoint): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('contrato.postContractMultipoint'), params);
  }

  public downloadTemplates(installations: boolean): Observable<any> {
    return this.http.get(this.config.getUrl(installations ? 'contrato.downloadTemplateMultipoint' : 'contrato.getContractTemplate'), {
      headers: installations ? { Accept: 'application/xlsx' } : { Accept: 'application/zip' },
      responseType: 'blob',
      observe: 'response'
    });
  }

  public mapperGetBusinessDraft(response): BusinessMultiPoint {
    return {
      canEdit: response.infoRazonSocial.editable,
      socialReason: response.infoRazonSocial.codigoRazonSocial,
      socialReasonLabel: response.infoRazonSocial.nombre,
      numberDocument: response.infoRazonSocial.cif,
      cnae: response.infoRazonSocial.cnae,
      contactName: response.infoRazonSocial.nombreContactoComercial,
      surname: response.infoRazonSocial.apellidosContactoComercial,
      dni: response.infoRazonSocial.dniContactoComercial,
      email: response.infoRazonSocial.mailContactoComercial,
      mobile: response.infoRazonSocial.telefonoContactoComercial,
      roadType: response.infoRazonSocial.nombreTipoViaDireccion,
      roadName: response.infoRazonSocial.nombreDireccion,
      addressNumber: response.infoRazonSocial.numeroDireccion,
      gate: response.infoRazonSocial.portalDireccion,
      stair: response.infoRazonSocial.escaleraDireccion,
      floor: response.infoRazonSocial.pisoDireccion,
      door: response.infoRazonSocial.puertaDireccion,
      postalCode: response.infoRazonSocial.codPostalDireccion,
      town: response.infoRazonSocial.codigoPoblacionDireccion,
      townLabel: response.infoRazonSocial.nombrePoblacionDireccion,
      province: response.infoRazonSocial.codigoProvinciaDireccion,
      provinceLabel: response.infoRazonSocial.nombreProvinciaDireccion
    };
  }

  public mapperGetPreInstallationDraft(response): PreviousInstallation {
    return {
      chargeManual: response.infoPrevioInstalacion.cargaManual,
      allAddressNotification: response.infoPrevioInstalacion.direccionNotificacionComun,
      allContactPerson: response.infoPrevioInstalacion.personaContactoComun,
      allAccountBank: response.infoPrevioInstalacion.cuentaBancariaComun,
      addressNotifRoadType: response.infoPrevioInstalacion.direccionNotificacionComunInfo.nombreTipoViaDireccion,
      addressNotifName: response.infoPrevioInstalacion.direccionNotificacionComunInfo.nombreDireccion,
      addressNotifNumber: response.infoPrevioInstalacion.direccionNotificacionComunInfo.numeroDireccion,
      addressNotifGate: response.infoPrevioInstalacion.direccionNotificacionComunInfo.portalDireccion,
      addressNotifStair: response.infoPrevioInstalacion.direccionNotificacionComunInfo.escaleraDireccion,
      addressNotifFloor: response.infoPrevioInstalacion.direccionNotificacionComunInfo.pisoDireccion,
      addressNotifDoor: response.infoPrevioInstalacion.direccionNotificacionComunInfo.puertaDireccion,
      addressNotifPostalCode: response.infoPrevioInstalacion.direccionNotificacionComunInfo.codPostalDireccion,
      addressNotifTown: response.infoPrevioInstalacion.direccionNotificacionComunInfo.codigoPoblacionDireccion,
      addressNotifProvince: response.infoPrevioInstalacion.direccionNotificacionComunInfo.codigoProvinciaDireccion,
      contactName: response.infoPrevioInstalacion.personaContactoComunInfo.nombrePersonaContacto,
      contactSurname: response.infoPrevioInstalacion.personaContactoComunInfo.apellidosPersonaContacto,
      contactDocumentNumber: response.infoPrevioInstalacion.personaContactoComunInfo.dniPersonaContacto,
      contactEmail: response.infoPrevioInstalacion.personaContactoComunInfo.telefonoPersonaContacto,
      contactMobile: response.infoPrevioInstalacion.personaContactoComunInfo.mailPersonaContacto,
      ibanCode: response.infoPrevioInstalacion.cuentaBancariaComunInfo.iban,
      ibanEntity: response.infoPrevioInstalacion.cuentaBancariaComunInfo.entidad,
      ibanOffice: response.infoPrevioInstalacion.cuentaBancariaComunInfo.sucursal,
      ibanDC: response.infoPrevioInstalacion.cuentaBancariaComunInfo.dc,
      ibanAccount: response.infoPrevioInstalacion.cuentaBancariaComunInfo.cuenta,
    };
  }

  public mapperGetInstallationDraft(response): InstallationMultiPoint[] {
    return response.infoInstalaciones.map((value, index) => ({
      groupIndex: index,
      name: value.nombre,
      cups: value.cups,
      cnae: value.cnae,
      code: value.codigoInstalacion,
      fee: value.tarifa,
      geographicZone: value.zonaGeograficaCUPS,
      cuPsAcepptedCRM: value.cuPsAceptadoEnCRM,
      codErrorAcepptedCRM: value.codErrorAceptadoEnCRM,
      descriptionErrorAcepptedCRM: value.descripcionErrorAceptadoEnCRM,
      codInstallation: value.codigoInstalacion,
      sendedCRM: value.enviadoCRM,
      registeredCRM: value.registroEnCRM,
      codErrorRegisteredCRM: value.codErrorRegistroEnCRM,
      descriptionErrorRegisteredCRM: value.descripcionErrorRegistroEnCRM,
      estimatedConsumptionPeriod: value.consumoEstimadoPeriodo,
      contractedPowerPeriod: value.potenciaContratadaPeriodo,
      energyChannelCommission: value.comisionCanalEnergia,
      commissionChannelPower: value.comisionCanalPotencia,
      consumptionEstimated: value.consumoEstimado,
      tension: value.tensionCUPS,
      period: value.periodoCUPS,
      cycle: value.cicloCUPS,
      periodName: value.nombrePeriodoCUPS,
      cycleName: value.nombreCicloCUPS,
      addressRoadType: value.direccionInstalacionInfo.nombreTipoViaDireccion,
      addressName: value.direccionInstalacionInfo.nombreDireccion,
      addressNumber: value.direccionInstalacionInfo.numeroDireccion,
      addressGate: value.direccionInstalacionInfo.portalDireccion,
      addressStair: value.direccionInstalacionInfo.escaleraDireccion,
      addressFloor: value.direccionInstalacionInfo.pisoDireccion,
      addressDoor: value.direccionInstalacionInfo.puertaDireccion,
      addressPostalCode: value.direccionInstalacionInfo.codPostalDireccion,
      addressTown: value.direccionInstalacionInfo.codigoPoblacionDireccion,
      addressProvince: value.direccionInstalacionInfo.codigoProvinciaDireccion,
      addressNotifRoadType: value.direccionNotificacionInfo.nombreTipoViaDireccion,
      addressNotifName: value.direccionNotificacionInfo.nombreDireccion,
      addressNotifNumber: value.direccionNotificacionInfo.numeroDireccion,
      addressNotifGate: value.direccionNotificacionInfo.portalDireccion,
      addressNotifStair: value.direccionNotificacionInfo.escaleraDireccion,
      addressNotifFloor: value.direccionNotificacionInfo.pisoDireccion,
      addressNotifDoor: value.direccionNotificacionInfo.puertaDireccion,
      addressNotifPostalCode: value.direccionNotificacionInfo.codPostalDireccion,
      addressNotifTown: value.direccionNotificacionInfo.codigoPoblacionDireccion,
      addressNotifProvince: value.direccionNotificacionInfo.codigoProvinciaDireccion,
      contactName: value.personaContactoInfo.nombrePersonaContacto,
      contactSurname: value.personaContactoInfo.apellidosPersonaContacto,
      contactDocumentNumber: value.personaContactoInfo.dniPersonaContacto,
      contactEmail: value.personaContactoInfo.mailPersonaContacto,
      contactMobile: value.personaContactoInfo.telefonoPersonaContacto,
      ibanCode: value.cuentaBancariaInfo.iban,
      ibanEntity: value.cuentaBancariaInfo.entidad,
      ibanOffice: value.cuentaBancariaInfo.sucursal,
      ibanDC: value.cuentaBancariaInfo.dc,
      ibanAccount: value.cuentaBancariaInfo.cuenta,
      enterMargins: {
        marginTP: value.margenTP,
        marginsTE: value.margenesTE
      }
    }));
  }

  public mapperGetSignerDraft(response): SignerMultiPoint {
    return {
      campaign: response.infoContrato.codigoCampana,
      campaignLabel: response.infoContrato.nombreCampana,
      level: response.infoContrato.nombreNivel,
      calculateMargins: response.infoContrato.calculateMargins,
      enterMargins: response.infoContrato.enterMargins,
      unitsTE: response.infoContrato.unitsTE,
      unitsTP: response.infoContrato.unitsTP,
      campaignBase: response.infoContrato.campanaBase,
      marginIndicator: response.infoContrato.indicadorMargen ? response.infoContrato.indicadorMargen.toString() : null,
      marginCups: response.infoContrato.marginCUPS,
      contactName: response.infoContrato.datosFirmanteInfo.nombreFirmante,
      contactSurname: response.infoContrato.datosFirmanteInfo.apellidosFirmante,
      contactDocumentNumber: response.infoContrato.datosFirmanteInfo.dniFirmante,
      contactEmail: response.infoContrato.datosFirmanteInfo.mailFirmante,
      contactMobile: response.infoContrato.datosFirmanteInfo.movilFirmante,
      invoiceDate: response.infoContrato.fechaVigenciaFutura,
      isMultibanco: response.infoContrato.isMultibanco || false,
      sameBankAccount: response.infoPrevioInstalacion.cuentaBancariaComun ? '0' : '1'
    };
  }

  public mapperSetInstallations(installations: InstallationMultiPoint[]): any[] {
    if (installations.length > 0) {
      return installations.map((item: InstallationMultiPoint) => ({
        nombre: item.name || null,
        cups: item.cups || null,
        cnae: item.cnae || null,
        tarifa: item.fee || null,
        tarifaSipReal: item.feeReal || null,
        zonaGeograficaCUPS: item.geographicZone || null,
        cuPsAceptadoEnCRM: item.cuPsAcepptedCRM || false,
        codErrorAceptadoEnCRM: item.codErrorAcepptedCRM || null,
        descripcionErrorAceptadoEnCRM: item.descriptionErrorAcepptedCRM || null,
        codigoInstalacion: item.codInstallation || null,
        enviadoCRM: item.sendedCRM || false,
        registroEnCRM: item.registeredCRM || false,
        codErrorRegistroEnCRM: item.codErrorRegisteredCRM || null,
        descripcionErrorRegistroEnCRM: item.descriptionErrorRegisteredCRM || null,
        consumoEstimadoPeriodo: item.estimatedConsumptionPeriod || null,
        potenciaContratadaPeriodo: item.contractedPowerPeriod || null,
        comisionCanalEnergia: item.energyChannelCommission || null,
        comisionCanalPotencia: item.commissionChannelPower || null,
        consumoEstimado: item.consumptionEstimated || null,
        tensionCups: item.tension || null,
        cicloCups: item.cycle || null,
        periodoCups: item.period || null,
        enterMargins: {
          marginTP: item.enterMargins?.marginTP || null,
          marginsTE: item.enterMargins?.marginsTE || null
        },
        direccionInstalacionInfo: {
          nombreTipoViaDireccion: item.addressRoadType || null,
          nombreDireccion: item.addressName || null,
          numeroDireccion: item.addressNumber || null,
          portalDireccion: item.addressGate || null,
          escaleraDireccion: item.addressStair || null,
          pisoDireccion: item.addressFloor || null,
          puertaDireccion: item.addressDoor || null,
          codPostalDireccion: item.addressPostalCode || null,
          codigoPoblacionDireccion: item.addressTown || null,
          codigoProvinciaDireccion: item.addressProvince || null
        },
        direccionNotificacionInfo: {
          nombreTipoViaDireccion: item.addressNotifRoadType || null,
          nombreDireccion: item.addressNotifName || null,
          numeroDireccion: item.addressNotifNumber || null,
          portalDireccion: item.addressNotifGate || null,
          escaleraDireccion: item.addressNotifStair || null,
          pisoDireccion: item.addressNotifFloor || null,
          puertaDireccion: item.addressNotifDoor || null,
          codPostalDireccion: item.addressNotifPostalCode || null,
          codigoPoblacionDireccion: item.addressNotifTown || null,
          codigoProvinciaDireccion: item.addressNotifProvince || null
        },
        cuentaBancariaInfo: {
          iban: item.ibanCode || null,
          entidad: item.ibanEntity || null,
          sucursal: item.ibanOffice || null,
          dc: item.ibanDC || null,
          cuenta: item.ibanAccount || null
        },
        personaContactoInfo: {
          nombrePersonaContacto: item.contactName || null,
          apellidosPersonaContacto: item.contactSurname || null,
          dniPersonaContacto: item.contactDocumentNumber || '',
          telefonoPersonaContacto: item.contactMobile || null,
          mailPersonaContacto: item.contactEmail || null
        }
      }));
    } else {
      return [ {
        nombre: null,
        cups: null,
        codigoInstalacion: null,
        tarifa: null,
        tarifaSipReal: null,
        zonaGeograficaCUPS: null,
        cuPsAceptadoEnCRM: false,
        codErrorAceptadoEnCRM: null,
        descripcionErrorAceptadoEnCRM: null,
        enviadoCRM: false,
        registroEnCRM: false,
        codErrorRegistroEnCRM: null,
        descripcionErrorRegistroEnCRM: null,
        direccionInstalacionInfo: {
          nombreTipoViaDireccion: null,
          nombreDireccion: null,
          numeroDireccion: null,
          portalDireccion: null,
          escaleraDireccion: null,
          pisoDireccion: null,
          puertaDireccion: null,
          codPostalDireccion: null,
          codigoPoblacionDireccion: null,
          codigoProvinciaDireccion: null
        },
        direccionNotificacionInfo: {
          nombreTipoViaDireccion: null,
          nombreDireccion: null,
          numeroDireccion: null,
          portalDireccion: null,
          escaleraDireccion: null,
          pisoDireccion: null,
          puertaDireccion: null,
          codPostalDireccion: null,
          codigoPoblacionDireccion: null,
          codigoProvinciaDireccion: null
        },
        cuentaBancariaInfo: {
          iban: null,
          entidad: null,
          sucursal: null,
          dc: null,
          cuenta: null
        },
        personaContactoInfo: {
          nombrePersonaContacto: null,
          apellidosPersonaContacto: null,
          dniPersonaContacto: null,
          telefonoPersonaContacto: null,
          mailPersonaContacto: null
        }
      } ];
    }
  }

  public mapperPostInstallation(value: InstallationMultiPoint): any {
    return {
      nombre: value.name,
      cups: value.cups,
      cnae: value.cnae,
      tarifa: value.fee,
      tarifaSipReal: value.feeReal,
      direccionInstalacionInfo: {
        nombreTipoViaDireccion: value.addressRoadType,
        nombreDireccion: value.addressName,
        numeroDireccion: value.addressNumber || '',
        portalDireccion: value.addressGate || '',
        escaleraDireccion: value.addressStair || '',
        pisoDireccion: value.addressFloor || '',
        puertaDireccion: value.addressDoor || '',
        codPostalDireccion: value.addressPostalCode,
        codigoPoblacionDireccion: value.addressTown,
        codigoProvinciaDireccion: value.addressProvince
      },
      personaContactoInfo: {
        nombrePersonaContacto: value.contactName,
        apellidosPersonaContacto: value.contactSurname,
        dniPersonaContacto: value.contactDocumentNumber,
        telefonoPersonaContacto: value.contactMobile,
        mailPersonaContacto: value.contactEmail,
      }
    };
  }

  public mapperSignerContract(signerData: SignerMultiPoint): any {
    return {
      codigoCampana: signerData?.campaign || null,
      nombreCampana: signerData?.campaignLabel || null,
      nombreNivel: signerData?.level || null,
      calculateMargins: {
        marginTP: {
          p1: parseFloat(signerData?.calculateMargins?.marginTP?.p1?.toString()?.replace(',', '.')) || null,
          p2: parseFloat(signerData?.calculateMargins?.marginTP?.p2?.toString()?.replace(',', '.')) || null,
          p3: parseFloat(signerData?.calculateMargins?.marginTP?.p3?.toString()?.replace(',', '.')) || null,
          p4: parseFloat(signerData?.calculateMargins?.marginTP?.p4?.toString()?.replace(',', '.')) || null,
          p5: parseFloat(signerData?.calculateMargins?.marginTP?.p5?.toString()?.replace(',', '.')) || null,
          p6: parseFloat(signerData?.calculateMargins?.marginTP?.p6?.toString()?.replace(',', '.')) || null
        },
        marginTE: {
          p1: parseFloat(signerData?.calculateMargins?.marginTE?.p1?.toString()?.replace(',', '.')) || null,
          p2: parseFloat(signerData?.calculateMargins?.marginTE?.p2?.toString()?.replace(',', '.')) || null,
          p3: parseFloat(signerData?.calculateMargins?.marginTE?.p3?.toString()?.replace(',', '.')) || null,
          p4: parseFloat(signerData?.calculateMargins?.marginTE?.p4?.toString()?.replace(',', '.')) || null,
          p5: parseFloat(signerData?.calculateMargins?.marginTE?.p5?.toString()?.replace(',', '.')) || null,
          p6: parseFloat(signerData?.calculateMargins?.marginTE?.p6?.toString()?.replace(',', '.')) || null
        }
      },
      unitsTE: signerData?.unitsTE || null,
      unitsTP: signerData?.unitsTP || null,
      campanaBase: signerData?.campaignBase || null,
      indicadorMargen: parseInt(signerData?.marginIndicator, 10) || null,
      marginCUPS: signerData?.marginCups || null,
      fechaVigenciaFutura: signerData?.invoiceDate ? this.utilService.formatDate(signerData?.invoiceDate) : null,
      datosFirmanteInfo: {
        nombreFirmante: signerData?.contactName || null,
        apellidosFirmante: signerData?.contactSurname || null,
        dniFirmante: signerData?.contactDocumentNumber || null,
        movilFirmante: signerData?.contactMobile || null,
        mailFirmante: signerData?.contactEmail || null
      },
      isMultibanco: signerData?.isMultibanco || false
    };
  }

  public postCheckIbanRS(params: CheckIbanSRRequest): Observable<GenericResponse<CheckIbanSRResponse>> {
    return this.http.post<GenericResponse<CheckIbanSRResponse>>(this.config.getUrl('maestros.postCheckIbanRS'), params);
  }
}
